import { delay } from "redux-saga";
import { call, put, select, takeLatest } from "redux-saga/effects";

import { CORPORATE_GROUP_ENVIRONMENT_TYPE } from "constants.js";
import * as transactionsMiddleware from "middleware/transactions";
import { push } from "react-router-redux";
import { types as loginTypes } from "reducers/login";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { actions, types, selectors as transactionsSelectors } from "reducers/transactions";
import { globalTypes } from "reducers/types/global";
import * as configUtil from "util/config";
import { downloadPdf, downloadXls } from "util/download";
import { adjustIdFieldErrors } from "util/form";
import * as i18n from "util/i18n";
import { MVP2_PENDING_TRANSACTIONS_CONFIG } from "util/transaction";

const sagas = [
    takeLatest([globalTypes.INIT, loginTypes.LOGIN_SUCCESS], refreshPendingTransactionsQuantity),
    takeLatest(types.LOAD_LIST_REQUEST, loadListRequest),
    takeLatest(types.DOWNLOAD_PENDING_TRANSACTIONS_REQUEST, downloadPendingTransactionsRequest),
    takeLatest(types.LOAD_TRANSACTION_ACTIVITIES_REQUEST, loadTransactionActivities),
    takeLatest(types.LOAD_MORE_TRANSACTIONS_REQUEST, fetchMoreTransactions),
    takeLatest(types.LOAD_MORE_TRANSACTIONS_REQUEST_MOBILE, fetchMoreTransactions),
    takeLatest(types.DELETE_DRAFT_REQUEST, deleteDraftRequest),
    takeLatest(types.SIGN_MULTIPLE_TRANSACTIONS, signMultipleTransaction),
    takeLatest(types.DELETE_MULTIPLE_TRANSACTIONS, deleteMultipleTransaction),
    takeLatest(types.POTENTIAL_SIGNATURES_REQUEST, potentialSignaturesRequest),
    takeLatest(types.IS_FIRST_TRANSACTION_CBU_REQUEST, isFirstTransactionCbuRequest),
    takeLatest(types.INIT_PENDINGS, initPendings),
    takeLatest(types.MULTIPLE_SIGNATURES_DATA_REQUEST, multipleSignatureData),
];

export default sagas;

const isMvp2FunctionalitiesEnabled = () => configUtil.getBoolean(MVP2_PENDING_TRANSACTIONS_CONFIG, false);

const getTransactionDataWithActivity = (selectedTransaction, fallbackActivityId, isApprovedFlowEnabled) => {
    const transactionData = selectedTransaction?.transaction?.data || {};
    if (!isApprovedFlowEnabled) {
        return transactionData;
    }

    const transactionActivityId = selectedTransaction?.transaction?.idActivity;
    const dataActivityId = transactionData?.idActivity;
    const idActivity = dataActivityId || transactionActivityId || fallbackActivityId;
    return idActivity ? { ...transactionData, idActivity } : transactionData;
};

const getTransactionDataWithActivityFromRows = (selectedTransactionRows, fallbackActivityId, isApprovedFlowEnabled) =>
    (selectedTransactionRows || []).map((selectedTransaction) =>
        getTransactionDataWithActivity(selectedTransaction, fallbackActivityId, isApprovedFlowEnabled),
    );

export function* refreshPendingTransactionsQuantity() {
    const activeEnvironment = yield select(sessionSelectors.getActiveEnvironment);
    if (activeEnvironment && activeEnvironment.type !== CORPORATE_GROUP_ENVIRONMENT_TYPE) {
        if (configUtil.getBoolean("feature.transactions.refreshPendingQuantity")) {
            while (true) {
                const hasActiveSession = yield select(sessionSelectors.isLoggedIn);
                if (!hasActiveSession) {
                    break;
                }

                try {
                    const response = yield call(transactionsMiddleware.getPendingTransactionsQuantity);
                    if (response.status !== 304 && response.type === "I") {
                        yield put({
                            type: types.REFRESH_PENDING_TRANSACTIONS_QUANTITY_SUCCESS,
                            pendingTransactionsQuantity: response.data.data.pendingTransactionsQuantity,
                        });
                    }
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                }
                yield call(delay, configUtil.get("transactions.pending.refreshRate", 60) * 1000);
            }
        }
    }
}

function* loadListRequest({ filters, onlyPendings, pendingDispatch }) {
    const response = yield call(transactionsMiddleware.loadListRequest, filters, onlyPendings, pendingDispatch);

    if (response.type === "W") {
        yield put(actions.loadListFailure());
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]));
    } else {
        const {
            transactions,
            pageNumber,
            totalPages,
            totalRows,
            controlActivity,
            controlARS,
            controlUSD,
        } = response.data.data;
        yield put(
            actions.loadListSuccess(
                transactions,
                pageNumber,
                totalPages,
                totalRows,
                controlActivity,
                controlARS,
                controlUSD,
            ),
        );
    }
}

function* downloadPendingTransactionsRequest({ filters, onlyPendings, pendingDispatch, format }) {
    try {
        const { type, data } = yield call(
            transactionsMiddleware.downloadPendingTransactionsRequest,
            filters,
            onlyPendings,
            pendingDispatch,
            format,
        );
        if (type === "W") {
            yield put({ type: types.DOWNLOAD_PENDING_TRANSACTIONS_FAILURE });
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]),
            );
        } else {
            const { content, fileName } = data.data;

            if (format === "pdf") {
                downloadPdf(fileName, content);
            } else {
                downloadXls(fileName, content);
            }

            yield put({ type: types.DOWNLOAD_PENDING_TRANSACTIONS_SUCCESS });
            yield put(
                notificationActions.showNotification(
                    i18n.get("global.successDownload"),
                    "success",
                    ["transactions"],
                    null,
                    false,
                ),
            );
        }
    } catch (error) {
        yield put({ type: types.DOWNLOAD_PENDING_TRANSACTIONS_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]));
    }
}

function* loadTransactionActivities() {
    const response = yield call(transactionsMiddleware.loadTransactionActivities);

    if (response.type === "W") {
        yield put(actions.loadTransactionActivitiesFailure());
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]));
    } else {
        const { activities } = response.data.data;
        yield put(actions.loadTransactionActivitiesSuccess(activities));
    }
}

function* fetchMoreTransactions({ filters, onlyPendings, pendingDispatch, isDesktop }) {
    const page = filters.pageNumber;
    const response = yield call(
        transactionsMiddleware.loadListRequest,
        { ...filters, pageNumber: page },
        onlyPendings,
        pendingDispatch,
    );

    if (response.type === "W") {
        yield put(actions.loadListFailure());
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]));
        return;
    }

    let {
        transactions,
        pageNumber: respPage,
        totalPages: respTotal,
        totalRows,
        controlActivity,
        controlARS,
        controlUSD,
    } = response.data.data;

    const requestedPage = page;
    const needsClamp = respTotal > 0 && requestedPage > respTotal;

    if (needsClamp) {
        const safePage = Math.min(requestedPage, respTotal);
        const response2 = yield call(
            transactionsMiddleware.loadListRequest,
            { ...filters, pageNumber: safePage },
            onlyPendings,
            pendingDispatch,
        );
        if (response2.type === "W") {
            yield put(actions.loadListFailure());
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]),
            );
            return;
        }
        ({
            transactions,
            pageNumber: respPage,
            totalPages: respTotal,
            totalRows,
            controlActivity,
            controlARS,
            controlUSD,
        } = response2.data.data);
    }

    if (isDesktop) {
        yield put(actions.loadMoreTransactionsSuccess(transactions, respPage, respTotal, totalRows));
    } else if (needsClamp) {
        yield put(
            actions.loadListSuccess(
                transactions,
                respPage,
                respTotal,
                totalRows,
                controlActivity,
                controlARS,
                controlUSD,
            ),
        );
    } else {
        yield put(actions.loadMoreTransactionsSuccessMobile(transactions, respPage, respTotal, totalRows));
    }
}

function* deleteDraftRequest({ idTransaction }) {
    const response = yield call(transactionsMiddleware.deleteDraftRequest, idTransaction);

    if (response.type === "W") {
        yield put(actions.deleteDraftFailure());
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]));
    } else {
        const { deleted } = response.data.data;

        if (deleted) {
            yield put(actions.deleteDraftSuccess(idTransaction));
            yield put(
                notificationActions.showNotification(i18n.get("transactions.list.draft.deleted"), "success", [
                    "transactions",
                ]),
            );
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("transactions.list.draft.deleted.fail"), "success", [
                    "transactions",
                ]),
            );
        }
    }
}

function* multipleSignatureData({ multipleSignaturesData }) {
    const idActivity = multipleSignaturesData?.activitySelected?.idActivity;
    const selectedTransactionRows = multipleSignaturesData?.selectedTransactionRows || [];
    const isApprovedFunctionalities = isMvp2FunctionalitiesEnabled();
    const transactions = getTransactionDataWithActivityFromRows(
        selectedTransactionRows,
        idActivity,
        isApprovedFunctionalities,
    );

    const { data, type } = yield call(transactionsMiddleware.multipleSignatureDataPreview, idActivity, transactions);
    if (type === "W") {
        if (data.code === "COR020W") {
            yield put(
                notificationActions.showNotification(
                    data?.data?.message,
                    "warning",
                    ["transactions"],
                    undefined,
                    false,
                ),
            );
        }
        yield put({ type: types.MULTIPLE_SIGNATURES_DATA_FAILURE });
    } else if (data.code === "COR005E") {
        yield put(notificationActions.showNotification(i18n.get("returnCode.COR005E"), "error", ["transactions"]));
        yield put({ type: types.MULTIPLE_SIGNATURES_DATA_FAILURE });
    } else {
        yield put(push("/pendingTransaction/signMultiple"));
        yield put({ type: types.MULTIPLE_SIGNATURES_DATA_SUCCESS });
    }
}

function* signMultipleTransaction({ payload }) {
    const { transactions, transactionsData, idActivity, otp, formikBag, userId, idForm } = payload;
    const isApprovedFunctionalities = isMvp2FunctionalitiesEnabled();
    try {
        const msData = yield select(transactionsSelectors.getMultipleSignaturesData);
        const selectedRows = yield select(transactionsSelectors.getSelectedTransactionRows);
        const previewIdActivity = msData?.activitySelected?.idActivity || idActivity;
        if (selectedRows && selectedRows.length > 0 && previewIdActivity) {
            const previewTransactions = getTransactionDataWithActivityFromRows(
                selectedRows,
                previewIdActivity,
                isApprovedFunctionalities,
            );
            const previewResp = yield call(
                transactionsMiddleware.multipleSignatureDataPreview,
                previewIdActivity,
                previewTransactions,
            );
            if (previewResp.type === "W" && previewResp.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(
                        previewResp.data?.data?.message,
                        "warning",
                        ["multipleSignatures"],
                        undefined,
                        false,
                    ),
                );
                formikBag.setSubmitting(false);
                return;
            }
        }
    } catch (e) {
        // en caso de error en preview, continuamos con flujo normal para no bloquear indebidamente
    }
    const selectedRows = yield select(transactionsSelectors.getSelectedTransactionRows);
    const transactionsDataWithActivity =
        selectedRows && selectedRows.length > 0
            ? getTransactionDataWithActivityFromRows(selectedRows, idActivity, isApprovedFunctionalities)
            : transactionsData;
    const { data, type } = yield call(
        transactionsMiddleware.signMultipleTransactions,
        idActivity,
        transactions,
        transactionsDataWithActivity,
        otp,
        userId,
        idForm,
    );
    formikBag.setSubmitting(false);
    if (type === "W") {
        if (data.code === "COR020W") {
            if (data?.data?.NO_FIELD) {
                yield put(
                    notificationActions.showNotification(data?.data?.NO_FIELD, "warning", ["multipleSignatures"]),
                );
            } else {
                formikBag.setErrors(adjustIdFieldErrors(data.data));
            }
        } else {
            yield put(notificationActions.showNotification(data.message, "error", ["multipleSignatures"]));
        }
    } else if (data.code === "COR005E") {
        yield put(
            notificationActions.showNotification(i18n.get("returnCode.COR005E"), "error", ["multipleSignatures"]),
        );
    } else {
        yield put(push(`/pendingTransaction/multipleSignatures/processing`));
        yield put(
            notificationActions.showNotification(i18n.get("multiple.signatures.notification.processing"), "warning", [
                "multipleSignaturesProcessing",
            ]),
        );
    }
}
function* deleteMultipleTransaction({ payload }) {
    const { transactions, idActivity, otp, formikBag, userId, idForm } = payload;
    const { data, type } = yield call(
        transactionsMiddleware.deleteMultipleTransactions,
        idActivity,
        transactions,
        otp,
        userId,
        idForm,
    );
    formikBag.setSubmitting(false);
    if (type === "W") {
        if (data.code === "COR020W") {
            if (data?.data?.NO_FIELD) {
                yield put(notificationActions.showNotification(data?.data?.NO_FIELD, "warning", ["multipleDelete"]));
            } else {
                formikBag.setErrors(adjustIdFieldErrors(data.data));
            }
        } else {
            yield put(notificationActions.showNotification(data.message, "error", ["multipleDelete"]));
        }
    } else if (data.code === "COR005E") {
        yield put(
            notificationActions.showNotification(i18n.get("returnCode.COR005E"), "error", ["multipleSignatures"]),
        );
    } else {
        yield put(push(`/pendingTransaction/multipleDelete/processing`));
        yield put(
            notificationActions.showNotification(i18n.get("multiple.delete.notification.processing"), "warning", [
                "multipleDeleteProcessing",
            ]),
        );
    }
}

function* potentialSignaturesRequest({ idTransaction }) {
    const response = yield call(transactionsMiddleware.potentialSignaturesRequest, idTransaction);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["transactions"]));
    } else {
        yield put({
            type: types.POTENTIAL_SIGNATURES_SUCCESS,
            potentialSignaturesData: response.data.data.potentialSignatures,
            serverDate: response.data.data.serverDate,
        });
    }
}

function* isFirstTransactionCbuRequest({ cbu, tadTrx }) {
    const dateFrom = new Date();
    dateFrom.setYear(dateFrom.getFullYear() - 10);
    const dateTo = new Date();

    const isCVU = cbu?.startsWith("000");
    const showCVUTransfers = configUtil.getBoolean("frontend.show.transfers.services.Coelsa.CVU.functionalities");
    let idActivity = "transfers.thirdParties.send";
    if (tadTrx) {
        idActivity = "transfers.thirdParties.tad.send";
    } else if (isCVU && showCVUTransfers) {
        idActivity = "transfersCVU.thirdParties.send";
    }

    const filters = {
        dateFrom,
        dateTo,
        pageNumber: 1,
        filter: "last",
        status: null,
        idActivity,
        filterPendingDispatch: false,
        functionRequest: "pendingTransactions",
    };

    const responseFrequentDestination = yield call(transactionsMiddleware.isCbuFrequentDestination, cbu);
    const frequentDestination = responseFrequentDestination.data.data?.CBUInFrequentDestination;
    let showSheduleNotification = !frequentDestination;

    if (showSheduleNotification) {
        const responseSummary = yield call(transactionsMiddleware.loadListRequest, filters, false, false);
        const summary = responseSummary.data.data?.transactions;
        let index = 0;
        let cont = 0;
        while (index < summary.length && showSheduleNotification && summary.length > 1) {
            if (summary[index].transaction.data.cbu === cbu) {
                cont += 1;
                if (cont > 1) {
                    showSheduleNotification = false;
                }
            }
            index += 1;
        }
    }

    if (showSheduleNotification) {
        yield put(
            notificationActions.showNotification(
                i18n.get("transfer.frequentDestinacions.notification.firstTransfer"),
                "warning",
                ["pendingTransfer"],
                i18n.get("transfer.frequentDestinacions.notification.firstTransfer.tittle"),
            ),
        );
    }
}

function* initPendings() {
    const status = "PENDING";
    const filters = {
        pageNumber: 1,
        filter: undefined,
        status,
        filterPendingDispatch: false,
        functionRequest: "pendingTransactions",
    };
    yield put({
        type: types.CHANGE_STATUS_TYPE,
        status,
    });
    yield put({
        type: types.LOAD_LIST_REQUEST,
        filters,
        onlyPendings: true,
        pendingDispatch: false,
    });
}

export function* hasTransactionHistoryWithCbu(cbu, idActivity) {
    const dateFrom = new Date();
    dateFrom.setYear(dateFrom.getFullYear() - 10);

    const filters = {
        dateFrom,
        dateTo: new Date(),
        pageNumber: 1,
        filter: "last",
        status: null,
        idActivity,
        filterPendingDispatch: false,
        functionRequest: "pendingTransactions",
    };
    const responseSummary = yield call(transactionsMiddleware.loadListRequest, filters, false, false);
    const transactions = responseSummary.data.data?.transactions || [];

    const matchingCount = transactions.filter((item) => item.transaction.data.cbu === cbu).length;
    return matchingCount > 1;
}
